import React from 'react';
import { observer } from 'owa-mobx-react';
import { navigationPaneLabel } from './MailModule.locstring.json';
import loc from 'owa-localize';
import {
    getDataMinMaxWidthsForLeftNav,
    shouldShowFolderPaneAsOverlay,
    shouldShowFolderPane,
    getLeftPaneStyles,
    toggleFolderPaneOnOrientationChange,
} from 'owa-mail-layout';
import { getScreenOrientation } from 'owa-layout';
import MailLeftPaneAsOverlay from './MailLeftPaneAsOverlay';
import FolderPaneContainer from './FolderPaneContainer';

import { leftPaneContainer, leftPanelSecondRow } from './MailModule.scss';

import classnames from 'owa-classnames';
import { useLeftPaneContentStyle } from 'owa-left-pane-layout/lib/utils/useLeftPaneContentStyle';
import type { FunctionComponent } from 'react';
import { type StateChangeSource } from 'owa-left-pane-layout/lib/types/StateChangeSource';

const defaultExport: FunctionComponent<{}> = observer(function MailLeftPane() {
    const showFolderPane = shouldShowFolderPane();
    const showFolderPaneAsOverlay = shouldShowFolderPaneAsOverlay();
    const leftPaneClasses = classnames(leftPaneContainer, getLeftPaneStyles());
    const { dataMin, dataMax } = getDataMinMaxWidthsForLeftNav();

    const memoizedLeftPaneStyle = useLeftPaneContentStyle(showFolderPane);
    const screenOrientationMedia: EventTarget =
        window.screen?.orientation ?? window.matchMedia('(orientation: portrait)');

    const handleOrientationChange = React.useCallback(() => {
        // On orientation change to portrait mode, if the folder pane is expanded, collapse it to be shown as overlay
        // On orientation change to landscape mode, if the folder pane was overlay switch back to expanded mode if width allows
        // Do not persist the state on server as this is specific to screen orientation that can be differ based on device and screen
        toggleFolderPaneOnOrientationChange(8, getScreenOrientation());
    }, []);

    React.useEffect(() => {
        // Add event listener for screen orientation change
        screenOrientationMedia?.addEventListener('change', handleOrientationChange);
        return () => {
            screenOrientationMedia?.removeEventListener('change', handleOrientationChange);
        };
    }, []);

    return (
        <div
            tabIndex={-1}
            role={'region'}
            aria-label={loc(navigationPaneLabel)}
            className={leftPaneClasses}
            data-min-width={dataMin}
            data-max-width={dataMax}
        >
            <div
                data-app-section="NavigationPane"
                className={classnames(leftPanelSecondRow, memoizedLeftPaneStyle)}
            >
                {renderFolderPane(showFolderPaneAsOverlay)}
            </div>
        </div>
    );
}, 'MailLeftPane');
export default defaultExport;

/**
 * Renders the folder pane as an overlay or non-overlay
 */
function renderFolderPane(showFolderPaneAsOverlay: boolean) {
    if (!showFolderPaneAsOverlay) {
        return <FolderPaneContainer />;
    }
    return <MailLeftPaneAsOverlay />;
}
